import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen,
  CheckCircle,
  CloudArrowUp,
  FileText,
  Microphone,
  Package,
  PenNib,
} from "@phosphor-icons/react";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui";
import {
  BookCover,
  PressEyebrow,
  PressPanel,
  StatusPill,
} from "@/components/pullpress/PressVisuals";

type JourneyStepId = "source" | "manuscript" | "editorial" | "channels" | "packet";

type JourneyStep = {
  readonly id: JourneyStepId;
  readonly eyebrow: string;
  readonly title: string;
  readonly body: string;
  readonly proof: string;
  readonly icon: typeof CloudArrowUp;
};

type PackageTier = {
  readonly name: string;
  readonly price: string;
  readonly cadence: string;
  readonly note: string;
  readonly cta: string;
  readonly href: string;
  readonly selected?: true;
};

type Partner = {
  readonly name: string;
  readonly logoSrc: string;
  readonly className?: string;
};

const JOURNEY_STEP_BY_ID: Readonly<Record<JourneyStepId, JourneyStep>> = {
  source: {
    id: "source",
    eyebrow: "1 · Source",
    title: "Bring what you have.",
    body: "Start with a draft, recording, outline, or manuscript. PullPress begins with the material that already exists.",
    icon: CloudArrowUp,
    proof: "TXT, DOCX, PDF, MP3, M4A",
  },
  manuscript: {
    id: "manuscript",
    eyebrow: "2 · Manuscript",
    title: "Read the book in chapters.",
    body: "The work becomes a manuscript you can read, revise, and approve before anything is released.",
    icon: BookOpen,
    proof: "Proof, chapters, notes",
  },
  editorial: {
    id: "editorial",
    eyebrow: "3 · Editorial",
    title: "Add judgment when the sentences need it.",
    body: "Use the software when the path is clear. Bring in an editor when the book needs a person.",
    icon: PenNib,
    proof: "Self-serve or editor-led",
  },
  channels: {
    id: "channels",
    eyebrow: "4 · Channels",
    title: "Prepare the files for the stores.",
    body: "PullPress prepares the publishing packet. You keep control of the store account, ISBN, and settings.",
    icon: Package,
    proof: "KDP, Apple, IngramSpark, Kobo",
  },
  packet: {
    id: "packet",
    eyebrow: "5 · Handoff",
    title: "Leave with the book in order.",
    body: "The manuscript, exports, details, and approval trail stay together so nobody rebuilds the book from memory.",
    icon: CheckCircle,
    proof: "EPUB, PDF, metadata, checklist",
  },
};

const JOURNEY_STEPS: readonly JourneyStep[] = [
  JOURNEY_STEP_BY_ID.source,
  JOURNEY_STEP_BY_ID.manuscript,
  JOURNEY_STEP_BY_ID.editorial,
  JOURNEY_STEP_BY_ID.channels,
  JOURNEY_STEP_BY_ID.packet,
];

const PACKAGES: readonly PackageTier[] = [
  {
    name: "Self-serve",
    price: "$997",
    cadence: "one-time package",
    note: "Choose this when the book has enough material and needs a clear path to a finished manuscript.",
    cta: "Start my book",
    href: "/signup?package=self-serve",
  },
  {
    name: "Editorial",
    price: "$1,997",
    cadence: "one-time package",
    note: "Choose this when the manuscript should pass through a real editor before readers see it.",
    cta: "Add my editor",
    href: "/signup?package=editorial",
    selected: true,
  },
];

const PARTNERS: readonly Partner[] = [
  {
    name: "Kindle Direct Publishing",
    logoSrc: "/provider-logos/kdp.svg",
    className: "max-h-9 max-w-[150px]",
  },
  {
    name: "Amazon",
    logoSrc: "/provider-logos/amazon.svg",
    className: "max-h-9 max-w-[126px]",
  },
  {
    name: "IngramSpark",
    logoSrc: "/provider-logos/ingramspark.png",
    className: "max-h-8 max-w-[150px]",
  },
  {
    name: "Barnes & Noble",
    logoSrc: "/provider-logos/barnes-noble.svg",
    className: "max-h-8 max-w-[150px]",
  },
  {
    name: "Apple Books",
    logoSrc: "/provider-logos/apple-books.svg",
    className: "max-h-9 max-w-[120px]",
  },
  {
    name: "Google Play Books",
    logoSrc: "/provider-logos/google-play-books.svg",
    className: "max-h-10 max-w-[86px]",
  },
  {
    name: "Rakuten Kobo",
    logoSrc: "/provider-logos/kobo.svg",
    className: "max-h-8 max-w-[136px]",
  },
];

const SOURCE_ITEMS: readonly { readonly title: string; readonly body: string }[] = [
  { title: "Drafts", body: "Bring the pages you already have." },
  { title: "Audio", body: "Turn recordings into usable source." },
  { title: "Notes", body: "Collect the outline before it scatters." },
];

const MANUSCRIPT_ROWS: readonly { readonly label: string; readonly value: string }[] = [
  { label: "Source material", value: "Captured" },
  { label: "Writing voice", value: "Mapped" },
  { label: "Chapter structure", value: "Built" },
  { label: "Manuscript proof", value: "Open" },
];

export function OnboardingPage() {
  return (
    <AppShell variant="public">
      <section className="border-b border-rule">
        <div className="mx-auto grid min-w-0 max-w-[1260px] gap-7 px-4 py-8 sm:px-6 lg:grid-cols-[0.9fr_1fr] lg:px-8 lg:py-10">
          <div className="min-w-0 flex flex-col justify-center">
            <PressEyebrow>A book is not a file.</PressEyebrow>
            <h1 className="mt-4 max-w-[650px] font-display text-[clamp(2.45rem,5vw,4.65rem)] font-semibold leading-[0.95] tracking-as-tight text-ink-1">
              Finish the book people have been asking you to write.
            </h1>
            <p className="mt-5 max-w-[560px] font-ui text-base font-semibold leading-as-read text-ink-2">
              PullPress turns your drafts, notes, and recordings into a manuscript you can read, edit, approve, and publish.
            </p>
            <p className="mt-3 max-w-[540px] font-text text-base leading-as-read text-ink-2">
              You are buying the day the book is finally in order and ready to leave your desk.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <Link to="/signup?package=self-serve">
                <Button variant="walnut" size="lg">Start my book</Button>
              </Link>
              <Link to="/pricing">
                <Button variant="secondary" size="lg" type="button">See pricing</Button>
              </Link>
            </div>
          </div>

          <HeroShowcase />
        </div>
      </section>

      <GuidedPathSection />
      <ClosingSection />
    </AppShell>
  );
}

function GuidedPathSection() {
  const [activeStepId, setActiveStepId] = useState<JourneyStepId>("source");
  const activeStep = JOURNEY_STEP_BY_ID[activeStepId];

  function selectStep(stepId: JourneyStepId) {
    setActiveStepId(stepId);
  }

  return (
    <section id="press-run" className="border-b border-rule bg-paper-raised">
      <div className="mx-auto max-w-[1260px] px-4 py-8 sm:px-6 lg:px-8 lg:py-10">
        <div className="grid gap-5 lg:grid-cols-[0.82fr_1.18fr] lg:items-end">
          <div>
            <PressEyebrow>One step at a time.</PressEyebrow>
            <h2 className="mt-3 max-w-[640px] font-display text-[clamp(1.8rem,3vw,3rem)] font-semibold leading-tight tracking-as-snug text-ink-1">
              The book gets a path without making you learn a system.
            </h2>
          </div>
          <p className="max-w-[600px] font-text text-base leading-as-read text-ink-2 lg:justify-self-end">
            The whole publishing process stays available, but only the next useful decision opens on the screen.
          </p>
        </div>

        <div className="mt-6 grid gap-5 lg:grid-cols-[360px_minmax(0,1fr)] lg:items-start">
          <div className="grid gap-2 lg:sticky lg:top-24" role="list" aria-label="Publishing path">
            {JOURNEY_STEPS.map((step) => (
              <JourneyStepButton
                key={step.id}
                step={step}
                isActive={step.id === activeStepId}
                onSelect={selectStep}
              />
            ))}
          </div>

          <div
            id={`journey-panel-${activeStep.id}`}
            className="min-w-0"
            aria-live="polite"
          >
            <JourneyDetail step={activeStep} />
          </div>
        </div>
      </div>
    </section>
  );
}

function JourneyStepButton({
  step,
  isActive,
  onSelect,
}: {
  readonly step: JourneyStep;
  readonly isActive: boolean;
  readonly onSelect: (stepId: JourneyStepId) => void;
}) {
  const Icon = step.icon;

  return (
    <button
      type="button"
      aria-expanded={isActive}
      aria-controls={`journey-panel-${step.id}`}
      onClick={() => onSelect(step.id)}
      className={`group grid min-h-[72px] w-full grid-cols-[40px_minmax(0,1fr)] gap-3 rounded-as-2 border px-3 py-3 text-left transition duration-as-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-walnut-soft sm:px-4 ${
        isActive
          ? "border-walnut bg-paper shadow-as-2"
          : "border-rule bg-paper-raised/60 hover:border-walnut-soft hover:bg-paper"
      }`}
    >
      <span className={`grid h-10 w-10 place-items-center rounded-full border ${
        isActive
          ? "border-walnut-soft bg-walnut-tint text-walnut-deep"
          : "border-rule bg-paper text-ink-3 group-hover:text-walnut"
      }`}
      >
        <Icon size={20} />
      </span>
      <span className="min-w-0">
        <span className="font-mono text-[9px] uppercase tracking-as-eyebrow text-ink-4">{step.eyebrow}</span>
        <span className="mt-1 block font-display text-lg font-semibold leading-tight text-ink-1">{step.title}</span>
        {isActive && (
          <span className="mt-2 block font-text text-sm leading-as-read text-ink-3">{step.body}</span>
        )}
      </span>
    </button>
  );
}

function JourneyDetail({ step }: { readonly step: JourneyStep }) {
  switch (step.id) {
    case "source":
      return <SourceDetail step={step} />;
    case "manuscript":
      return <ManuscriptDetail step={step} />;
    case "editorial":
      return <EditorialDetail step={step} />;
    case "channels":
      return <ChannelsDetail step={step} />;
    case "packet":
      return <PacketDetail step={step} />;
    default:
      return assertUnreachable(step.id);
  }
}

function SourceDetail({ step }: { readonly step: JourneyStep }) {
  return (
    <PressPanel className="p-5 sm:p-6">
      <DetailHeader step={step} />
      <div className="mt-6 grid gap-3 sm:grid-cols-3">
        {SOURCE_ITEMS.map((item) => (
          <div key={item.title} className="rounded-as-2 border border-rule bg-paper-raised px-4 py-4">
            <h3 className="font-display text-lg font-semibold leading-tight text-ink-1">{item.title}</h3>
            <p className="mt-2 font-text text-sm leading-as-read text-ink-3">{item.body}</p>
          </div>
        ))}
      </div>
      <div className="mt-5 rounded-as-2 border border-rule bg-paper p-3 font-mono text-[10px] uppercase tracking-[0.14em] text-ink-3">
        {step.proof}
      </div>
    </PressPanel>
  );
}

function ManuscriptDetail({ step }: { readonly step: JourneyStep }) {
  return (
    <PressPanel className="p-5 sm:p-6">
      <DetailHeader step={step} />
      <div className="mt-6 grid gap-5 sm:grid-cols-[110px_minmax(0,1fr)] sm:items-start">
        <BookCover title="Field Notes" subtitle="A working book" author="Jane Smith" tone="mountain" className="w-28" />
        <div className="divide-y divide-rule-faint rounded-as-2 border border-rule bg-paper">
          {MANUSCRIPT_ROWS.map((row, index) => (
            <div key={row.label} className="flex items-center justify-between gap-4 px-4 py-3">
              <span className="font-ui text-sm font-semibold text-ink-2">{row.label}</span>
              <span className={`font-mono text-[9px] uppercase tracking-as-eyebrow ${index < 3 ? "text-verify" : "text-walnut-deep"}`}>
                {row.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    </PressPanel>
  );
}

function EditorialDetail({ step }: { readonly step: JourneyStep }) {
  return (
    <div>
      <DetailHeader step={step} />
      <div className="mt-5 grid gap-3 md:grid-cols-2">
        {PACKAGES.map((tier) => (
          <PressPanel key={tier.name} className={`relative flex flex-col p-5 ${tier.selected ? "border-walnut shadow-as-2" : ""}`}>
            {tier.selected && <div className="absolute right-4 top-4"><StatusPill tone="editing">Popular</StatusPill></div>}
            <h3 className="font-display text-2xl font-semibold text-ink-1">{tier.name}</h3>
            <div className="mt-4">
              <span className="font-display text-4xl font-semibold tracking-as-tight text-ink-1">{tier.price}</span>
              <span className="ml-2 align-middle font-mono text-[10px] uppercase tracking-as-eyebrow text-ink-4">{tier.cadence}</span>
            </div>
            <p className="mt-4 min-h-[72px] font-text text-sm leading-as-read text-ink-2">{tier.note}</p>
            <Link to={tier.href} className="mt-5 block">
              <Button variant={tier.selected ? "walnut" : "secondary"} className="w-full">{tier.cta}</Button>
            </Link>
          </PressPanel>
        ))}
      </div>
    </div>
  );
}

function ChannelsDetail({ step }: { readonly step: JourneyStep }) {
  return (
    <PressPanel className="p-5 sm:p-6">
      <DetailHeader step={step} />
      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {PARTNERS.map((partner) => (
          <PartnerLogo key={partner.name} partner={partner} />
        ))}
      </div>
    </PressPanel>
  );
}

function PacketDetail({ step }: { readonly step: JourneyStep }) {
  return (
    <div className="grid gap-3 lg:grid-cols-[1fr_0.78fr]">
      <PressPanel className="p-5 sm:p-6">
        <DetailHeader step={step} />
        <div className="mt-5 divide-y divide-rule-faint">
          <ReleaseRow label="Manuscript proof" value="Complete" />
          <ReleaseRow label="Editorial trail" value="Attached" />
          <ReleaseRow label="EPUB and PDF files" value="Prepared" />
          <ReleaseRow label="Book details and listing copy" value="Queued" warning />
        </div>
      </PressPanel>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
        <HandoffNote title="Release files." body="The manuscript, EPUB, PDF, and details stay together." icon={<FileText size={19} />} />
        <HandoffNote title="Approval record." body="You can see what is approved and what still needs work." icon={<CheckCircle size={19} />} />
      </div>
    </div>
  );
}

function DetailHeader({ step }: { readonly step: JourneyStep }) {
  const Icon = step.icon;

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div className="max-w-[620px]">
        <div className="flex items-center gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-full border border-walnut-soft bg-walnut-tint text-walnut-deep">
            <Icon size={20} />
          </span>
          <PressEyebrow>{step.eyebrow}</PressEyebrow>
        </div>
        <h3 className="mt-4 font-display text-[clamp(1.55rem,2.2vw,2.35rem)] font-semibold leading-tight tracking-as-snug text-ink-1">
          {step.title}
        </h3>
        <p className="mt-3 font-text text-base leading-as-read text-ink-2">{step.body}</p>
      </div>
      <span className="w-fit rounded-full border border-rule bg-paper px-3 py-2 font-mono text-[9px] uppercase tracking-as-eyebrow text-ink-4">
        {step.proof}
      </span>
    </div>
  );
}

function ClosingSection() {
  return (
    <section className="border-b border-rule bg-paper">
      <div className="mx-auto flex max-w-[1260px] flex-col gap-5 px-4 py-8 sm:px-6 md:flex-row md:items-center md:justify-between lg:px-8">
        <div>
          <PressEyebrow>Ready when the book is ready.</PressEyebrow>
          <h2 className="mt-3 max-w-[640px] font-display text-[clamp(1.7rem,2.5vw,2.6rem)] font-semibold leading-tight tracking-as-snug text-ink-1">
            Start with the material you already have.
          </h2>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row md:shrink-0">
          <Link to="/signup?package=self-serve">
            <Button variant="walnut" size="lg" className="w-full sm:w-auto">Start my book</Button>
          </Link>
          <Link to="/pricing">
            <Button variant="secondary" size="lg" className="w-full sm:w-auto" type="button">See pricing</Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

function HandoffNote({ title, body, icon }: { readonly title: string; readonly body: string; readonly icon: ReactNode }) {
  return (
    <div className="rounded-as-2 border border-rule bg-paper-raised p-4">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 text-walnut">{icon}</div>
        <div>
          <h3 className="font-display text-lg font-semibold leading-tight text-ink-1">{title}</h3>
          <p className="mt-1 font-ui text-sm leading-as-read text-ink-3">{body}</p>
        </div>
      </div>
    </div>
  );
}

function ReleaseRow({
  label,
  value,
  warning = false,
}: {
  readonly label: string;
  readonly value: string;
  readonly warning?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-2">
      <span className="font-ui text-sm text-ink-2">{label}</span>
      <span className={`font-mono text-[9px] uppercase tracking-as-eyebrow ${warning ? "text-walnut-deep" : "text-verify"}`}>{value}</span>
    </div>
  );
}

function HeroShowcase() {
  return (
    <div
      className="relative min-h-[300px] min-w-0 overflow-hidden rounded-as-3 border border-[color:color-mix(in_oklab,var(--as-paper-raised)_76%,transparent)] bg-paper-raised/90 shadow-as-2 backdrop-blur-2xl lg:min-h-[430px]"
      aria-label="PullPress turns source material into a finished book packet."
    >
      <div className="grid min-h-[300px] min-w-0 gap-4 p-4 sm:p-6 lg:min-h-[430px] lg:grid-cols-[0.78fr_1fr] lg:items-center">
        <div className="hidden min-w-0 gap-3 lg:grid">
          <VisualFragment icon={<Microphone size={19} />} tone="voice" />
          <VisualFragment icon={<PenNib size={19} />} tone="notes" />
          <VisualFragment icon={<FileText size={19} />} tone="pages" />
        </div>

        <div className="grid min-w-0 gap-3 self-center">
          <div className="rounded-as-3 border border-rule bg-paper p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <PressEyebrow>The book is becoming real.</PressEyebrow>
                <h3 className="mt-2 font-display text-2xl font-semibold leading-tight text-ink-1">Field Notes</h3>
              </div>
              <StatusPill tone="editing">Proof</StatusPill>
            </div>
            <div className="mt-4 grid min-w-0 gap-4 sm:grid-cols-[96px_minmax(0,1fr)]">
              <BookCover title="Field Notes" subtitle="A working book" author="Jane Smith" tone="mountain" className="w-24" />
              <div className="space-y-2">
                {["The source is captured.", "The voice is mapped.", "The manuscript is built.", "The proof is open."].map((label, index) => (
                  <div key={label} className="flex items-center justify-between rounded-as-2 border border-rule bg-paper-raised px-3 py-2">
                    <span className="font-ui text-sm font-semibold text-ink-2">{label}</span>
                    <span className={`h-2 w-2 rounded-full ${index < 3 ? "bg-verify" : "bg-walnut"}`} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <HeroMetric label="Packet" value="EPUB · PDF" />
            <HeroMetric label="Channels" value="KDP + Apple" />
            <HeroMetric label="Next" value="Approve proof" />
          </div>
        </div>
      </div>
    </div>
  );
}

function HeroMetric({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div className="rounded-as-2 border border-rule bg-paper p-3">
      <p className="font-mono text-[9px] uppercase tracking-as-eyebrow text-ink-4">{label}</p>
      <p className="mt-1 font-ui text-sm font-semibold text-ink-1">{value}</p>
    </div>
  );
}

function VisualFragment({ icon, tone }: { readonly icon: ReactNode; readonly tone: "voice" | "notes" | "pages" }) {
  const lineCount = tone === "voice" ? 5 : tone === "notes" ? 4 : 6;
  return (
    <div className="rounded-as-3 border border-rule bg-paper-raised/90 p-3 shadow-as-1">
      <div className="flex items-center gap-3">
        <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-walnut-soft bg-walnut-tint text-walnut-deep">
          {icon}
        </div>
        <div className="flex-1 space-y-1.5">
          {Array.from({ length: lineCount }, (_, index) => (
            <SourceLine key={index} width={index % 3 === 0 ? "w-[92%]" : index % 3 === 1 ? "w-[74%]" : "w-[58%]"} />
          ))}
        </div>
      </div>
    </div>
  );
}

function SourceLine({ width }: { readonly width: string }) {
  return <div className={`h-1.5 rounded-full bg-rule-strong ${width}`} />;
}

function PartnerLogo({ partner }: { readonly partner: Partner }) {
  return (
    <div
      className="grid min-h-[68px] place-items-center rounded-as-2 border border-rule bg-paper-raised px-3 py-3 transition-colors duration-as-1 hover:border-walnut-soft"
      aria-label={partner.name}
      title={partner.name}
    >
      <img
        src={partner.logoSrc}
        alt={partner.name}
        className={`h-auto w-full object-contain ${partner.className ?? "max-h-9 max-w-[140px]"}`}
        loading="eager"
        decoding="async"
      />
    </div>
  );
}

function assertUnreachable(value: never): never {
  throw new Error(`Unhandled PullPress journey step: ${value}`);
}
